import { clientStore } from "./clientStorage";

export interface PersistenceFlushReceipt {
  participant: string;
  dirty: boolean;
  durable: boolean;
  reason?: string;
}

export interface PersistenceBarrierResult {
  safe: boolean;
  mutationEpoch: number;
  receipts: PersistenceFlushReceipt[];
  durability: ReturnType<typeof clientStore.durability>;
}

type FlushParticipant = () => PersistenceFlushReceipt;

interface RegisteredParticipant {
  participant: string;
  flush: FlushParticipant;
}

const participants = new Map<symbol, RegisteredParticipant>();
// A participant can unmount while manifest revalidation is in flight. If its
// cleanup cannot prove durability, retain the exact closure so the later
// barrier retries it instead of forgetting the dirty work.
const detachedDirtyParticipants = new Map<symbol, RegisteredParticipant>();
let mutationEpoch = 0;

/** Mark a user-visible persisted value as newer than the last durable receipt. */
export function markPersistenceMutation(): number {
  mutationEpoch += 1;
  return mutationEpoch;
}

/**
 * Register one mounted persistence producer. Tokens are per instance: two
 * mounted consumers with the same logical key must both prove their own state.
 */
export function registerPersistenceFlushParticipant(
  participant: string,
  flush: FlushParticipant
): () => void {
  const token = Symbol(participant);
  const registration = { participant, flush };
  participants.set(token, registration);
  return () => {
    participants.delete(token);
    try {
      const receipt = flush();
      if (receipt.dirty && !receipt.durable) {
        detachedDirtyParticipants.set(token, registration);
      } else {
        detachedDirtyParticipants.delete(token);
      }
    } catch {
      detachedDirtyParticipants.set(token, {
        participant,
        flush: () => ({
          participant,
          dirty: true,
          durable: false,
          reason: "detached-flush-threw",
        }),
      });
    }
  };
}

/**
 * Synchronous by design. Yielding between the first receipt and reload would
 * let another input become dirty after it had already been declared safe.
 */
export function flushPersistenceBeforeReload(): PersistenceBarrierResult {
  const startEpoch = mutationEpoch;
  const receipts: PersistenceFlushReceipt[] = [];
  const registrations = [
    ...Array.from(participants.entries()),
    ...Array.from(detachedDirtyParticipants.entries()),
  ];
  for (const [token, registration] of registrations) {
    try {
      const receipt = registration.flush();
      receipts.push(receipt);
      if (receipt.durable) detachedDirtyParticipants.delete(token);
    } catch {
      receipts.push({
        participant: registration.participant,
        dirty: true,
        durable: false,
        reason: "flush-threw",
      });
    }
  }
  const durability = clientStore.durability();
  const epochStable = startEpoch === mutationEpoch;
  return {
    safe:
      epochStable &&
      durability.mode === "durable" &&
      receipts.every((receipt) => !receipt.dirty || receipt.durable),
    mutationEpoch,
    receipts,
    durability,
  };
}

export const __testing = {
  reset() {
    participants.clear();
    detachedDirtyParticipants.clear();
    mutationEpoch = 0;
  },
  participantCount() {
    return participants.size;
  },
  detachedParticipantCount() {
    return detachedDirtyParticipants.size;
  },
};
